import UploadFileIcon from '@mui/icons-material/UploadFile'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { CaseDocumentsDto } from '@lawyer/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { ErrorAlert, LoadingBlock } from '../common/Feedback'
import { DocumentList } from './DocumentList'
import { FileDropZone } from './FileDropZone'
import { UploadDocumentDialog } from './UploadDocumentDialog'

type Props = {
  caseId: string
  clients: { clientId: string; clientName: string }[]
}

/**
 * Case documents: the case-wide ones first, then a group per client that also shows
 * that client's general documents tagged "מכרטיס הלקוח" (spec §5.1).
 */
export function CaseDocumentsPanel({ caseId, clients }: Props) {
  const queryClient = useQueryClient()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [droppedFile, setDroppedFile] = useState<File | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.caseDocuments(caseId),
    queryFn: () => api.get<CaseDocumentsDto>(`/cases/${caseId}/documents`),
  })

  const refresh = () => {
    void refetch()
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
  }

  const openUpload = (file: File | null) => {
    setDroppedFile(file)
    setUploadOpen(true)
  }

  return (
    <FileDropZone onFile={openUpload}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button startIcon={<UploadFileIcon />} onClick={() => openUpload(null)}>
          העלאת מסמך
        </Button>
      </Stack>

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        <Stack spacing={3}>
          <Paper variant="outlined">
            <Typography variant="subtitle2" sx={{ px: 2, py: 1.5 }}>
              מסמכי התיק
            </Typography>
            <Divider />
            <DocumentList
              documents={data.caseDocuments}
              emptyText="אין מסמכים כלליים בתיק"
              onChanged={refresh}
            />
          </Paper>

          {data.byClient.map((group) => (
            <Paper key={group.clientId} variant="outlined">
              <Typography variant="subtitle2" sx={{ px: 2, py: 1.5 }}>
                {group.clientName}
              </Typography>
              <Divider />
              <DocumentList
                documents={group.documents}
                emptyText="אין מסמכים ללקוח זה"
                onChanged={refresh}
              />
            </Paper>
          ))}
        </Stack>
      ) : null}

      <UploadDocumentDialog
        open={uploadOpen}
        scope={{ caseId }}
        clientChoices={clients.map((client) => ({ id: client.clientId, label: client.clientName }))}
        defaultFile={droppedFile}
        onClose={() => setUploadOpen(false)}
        onUploaded={refresh}
      />
    </FileDropZone>
  )
}
